import { useEffect } from "react";
import { useLocation } from "react-router";
import { create } from "zustand";
import { useOrgContextStore } from "@/components/main/stores/orgContextStore";
import { SHELL_PAGES, shellPageName } from "./pages";

const HOME = SHELL_PAGES.Overview.path;

interface ShellVisit {
  pathname: string;
  to: string;
  /// The Overview's space filter, which lives in the store, not the URL.
  spaceId: string | null;
}

/// In memory, not sessionStorage, so a reload or a new tab has no way back.
export const useLastShellPageStore = create<{ page: ShellVisit | null }>(
  () => ({ page: null }),
);

/// Mounted by the shell only, so models visited on the way are never recorded.
export function useRememberShellPage(): void {
  const { pathname, search, hash } = useLocation();
  const selectedSpaceId = useOrgContextStore((s) => s.selectedSpaceId);
  const spaceId = pathname === HOME ? selectedSpaceId : null;
  useEffect(() => {
    useLastShellPageStore.setState({
      page: { pathname, to: pathname + search + hash, spaceId },
    });
  }, [pathname, search, hash, spaceId]);
}

/// None from the whole Overview, which Home already returns to.
export function canvasBack(): {
  to: string;
  spaceId: string | null;
  title: string;
} | null {
  const page = useLastShellPageStore.getState().page;
  if (!page) return null;
  if (page.pathname === HOME) {
    const space = useOrgContextStore.getState().spaceById(page.spaceId);
    return space
      ? {
          to: page.to,
          spaceId: space.space_id,
          title: `Go back to ${space.name}`,
        }
      : null;
  }
  const name = shellPageName(page.pathname);
  return {
    to: page.to,
    spaceId: null,
    title: name ? `Go back to ${name}` : "Go back",
  };
}

export function canvasExit(): string {
  return useLastShellPageStore.getState().page?.to ?? HOME;
}
